/**
 * Sound Manifest Service
 * Loads and manages phoneme-based sound files from manifest.json
 */

/**
 * Letter sound entry (letter names like "ay", "bee", "see")
 */
export interface LetterSound {
  file: string;
  letter: string;
  pronunciation: string;
}

/**
 * Phoneme sound entry (letter sounds like short "a", long "a")
 */
export interface PhonemeSound {
  file: string;
  letter: string;
  type: string;
  ipa: string;
  example: string;
}

/**
 * Combination/digraph sound entry (ch, sh, th, etc.)
 */
export interface CombinationSound {
  file: string;
  letter: string;
  type: string;
  ipa: string;
  example: string;
}

/**
 * Complete sound manifest structure
 */
export interface SoundManifest {
  generated: string;
  model: string;
  voice: string;
  letters: LetterSound[];
  phonemes: PhonemeSound[];
}

/**
 * Sound Manifest Service Class
 * Singleton service for loading and looking up sound files
 */
export class SoundManifestService {
  private manifest: SoundManifest | null = null;
  private letterMap: Map<string, LetterSound> = new Map();
  private phonemeMap: Map<string, PhonemeSound[]> = new Map();
  private combinationMap: Map<string, CombinationSound[]> = new Map();
  private isLoaded: boolean = false;
  private loadError: Error | null = null;

  /**
   * Load the sound manifest from JSON file
   */
  async load(): Promise<void> {
    try {
      console.log('[SoundManifest] Loading sound manifest...');

      // Fetch the manifest file
      const response = await fetch('/src/assets/sounds/manifest.json');
      if (!response.ok) {
        throw new Error(`Failed to fetch manifest: ${response.statusText}`);
      }

      const data = await response.json();
      this.manifest = data;

      // Build lookup maps for efficient access
      this.buildLetterMap();
      this.buildPhonemeMap();
      this.buildCombinationMap();

      this.isLoaded = true;
      console.log(
        `[SoundManifest] Loaded ${this.letterMap.size} letters, ` +
          `${this.phonemeMap.size} phoneme letters, ` +
          `${this.combinationMap.size} combinations`
      );
    } catch (error) {
      this.loadError = error instanceof Error ? error : new Error(String(error));
      console.error('[SoundManifest] Failed to load manifest:', error);
      // Don't throw - allow graceful degradation
    }
  }

  /**
   * Build letter name lookup map (A → "letters/A.mp3")
   */
  private buildLetterMap(): void {
    if (!this.manifest) return;

    this.letterMap.clear();
    for (const letter of this.manifest.letters) {
      this.letterMap.set(letter.letter.toLowerCase(), letter);
    }
  }

  /**
   * Build phoneme lookup map (a → [short a, long a, ah])
   */
  private buildPhonemeMap(): void {
    if (!this.manifest) return;

    this.phonemeMap.clear();
    for (const phoneme of this.manifest.phonemes) {
      const letter = phoneme.letter.toLowerCase();

      // Skip combinations (they go in combinationMap)
      if (letter.length > 1) {
        continue;
      }

      if (!this.phonemeMap.has(letter)) {
        this.phonemeMap.set(letter, []);
      }
      this.phonemeMap.get(letter)!.push(phoneme);
    }
  }

  /**
   * Build combination/digraph lookup map (ch, sh, th)
   */
  private buildCombinationMap(): void {
    if (!this.manifest) return;

    this.combinationMap.clear();
    for (const phoneme of this.manifest.phonemes) {
      const letter = phoneme.letter.toLowerCase();

      // Only combinations (multi-character)
      if (letter.length <= 1) {
        continue;
      }

      if (!this.combinationMap.has(letter)) {
        this.combinationMap.set(letter, []);
      }
      this.combinationMap.get(letter)!.push(phoneme as CombinationSound);
    }
  }

  /**
   * Get file path for letter name sound
   * @param letter - Single letter (case-insensitive)
   * @returns File path relative to /src/assets/sounds/ or null if not found
   */
  getLetterNameSound(letter: string): string | null {
    const key = letter.toLowerCase();
    const entry = this.letterMap.get(key);
    return entry ? entry.file : null;
  }

  /**
   * Get file path for letter phoneme sound
   * @param letter - Single letter (case-insensitive)
   * @param type - Optional type filter (e.g., "short vowel", "long vowel", "consonant")
   * @returns File path or null if not found
   */
  getLetterPhonemeSound(letter: string, type?: string): string | null {
    const key = letter.toLowerCase();
    const phonemes = this.phonemeMap.get(key);

    if (!phonemes || phonemes.length === 0) {
      return null;
    }

    // If type specified, try to find matching phoneme
    if (type) {
      const typeKey = type.toLowerCase();
      const match = phonemes.find(p => p.type.toLowerCase().includes(typeKey));
      if (match) {
        return match.file;
      }
    }

    // Default: return first phoneme
    return phonemes[0].file;
  }

  /**
   * Get file path for combination/digraph sound
   * @param combo - Combination string (e.g., "ch", "sh", "th")
   * @param type - Optional type filter (e.g., "voiced", "unvoiced")
   * @returns File path or null if not found
   */
  getCombinationSound(combo: string, type?: string): string | null {
    const key = combo.toLowerCase();
    const combinations = this.combinationMap.get(key);

    if (!combinations || combinations.length === 0) {
      return null;
    }

    // If type specified, try to find matching combination
    if (type) {
      const typeKey = type.toLowerCase();
      const match = combinations.find(c => c.type.toLowerCase().includes(typeKey));
      if (match) {
        return match.file;
      }
    }

    // Default: return first combination
    return combinations[0].file;
  }

  /**
   * Get all phonemes for a letter (for advanced features)
   */
  getAllPhonemesForLetter(letter: string): PhonemeSound[] {
    const key = letter.toLowerCase();
    return this.phonemeMap.get(key) || [];
  }

  /**
   * Check if manifest is loaded
   */
  isManifestLoaded(): boolean {
    return this.isLoaded;
  }

  /**
   * Get load error if any
   */
  getLoadError(): Error | null {
    return this.loadError;
  }

  /**
   * Get full file path with base URL
   * @param relativePath - Path from manifest (e.g., "letters/A.mp3")
   * @returns Full path to audio file
   */
  getFullPath(relativePath: string): string {
    return `/src/assets/sounds/${relativePath}`;
  }
}

// Singleton instance for global use
export const soundManifest = new SoundManifestService();
